import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";

import { PROPOSITION } from "../services/api/Resolver";
import { getCookieValue, setCookie } from "../services/utils/cookies";
import { getTheme, logoURL } from "../theme";

type ThemeMode = "light" | "dark";

type ThemeContextType = {
  themeMode: ThemeMode;
  toggleThemeMode: () => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const preferredThemeMode = getCookieValue("themeMode") as ThemeMode | null;
  const [themeMode, setThemeMode] = useState<ThemeMode>(preferredThemeMode || "light");

  const toggleThemeMode = () => {
    setThemeMode((prevState) => {
      const newState = prevState === "dark" ? "light" : "dark";
      setCookie("themeMode", newState, 365 * 10);
      return newState;
    });
  };

  const theme = useMemo(() => getTheme(PROPOSITION, themeMode), [themeMode]);

  useEffect(() => {
    const link = document.createElement("link");
    link.href = logoURL;
    link.rel = "icon";
    document.getElementsByTagName("head")[0].appendChild(link);
  }, []);

  const contextValue = useMemo(() => ({ themeMode, toggleThemeMode }), [themeMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
